// Single source of truth for the Talmia activity taxonomy — icons, colours
// and rules shared by the composer, the Deal-Pipeline cell, Customer 360,
// the Quotes table and the Quote Builder. Mirrors backend ACTIVITY_TYPES.
import {
  PhoneCall, Mail, CalendarClock, AlarmClock, FileText, PoundSterling, Truck,
  MapPin, Wrench, Boxes, Repeat, Shuffle, TrendingUp, MessageSquare, StickyNote,
  Video, ListTodo,
} from "lucide-react";

// Creatable types (order = pill order in the composer).
export const ACTIVITY_TYPES = [
  "Call", "Email", "Follow-up", "Chase", "Quote", "Payment", "Delivery",
  "Site visit", "Technical", "Stock check", "Switch", "Cross-sell",
  "Account upgrade", "Other",
];

// Types that MUST carry a due date (they schedule future work).
export const REQUIRE_DUE = new Set(["Follow-up", "Chase", "Payment"]);

// Static Tailwind class strings (kept literal so JIT never purges them).
const TONES = {
  sky:     "bg-sky-500/15 text-sky-300 border-sky-500/40",
  blue:    "bg-blue-500/15 text-blue-300 border-blue-500/40",
  purple:  "bg-purple-500/15 text-purple-300 border-purple-500/40",
  amber:   "bg-amber-500/15 text-amber-300 border-amber-500/40",
  teal:    "bg-teal-500/15 text-teal-300 border-teal-500/40",
  emerald: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
  orange:  "bg-orange-500/15 text-orange-300 border-orange-500/40",
  rose:    "bg-rose-500/15 text-rose-300 border-rose-500/40",
  cyan:    "bg-cyan-500/15 text-cyan-300 border-cyan-500/40",
  indigo:  "bg-indigo-500/15 text-indigo-300 border-indigo-500/40",
  fuchsia: "bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/40",
  violet:  "bg-violet-500/15 text-violet-300 border-violet-500/40",
  yellow:  "bg-yellow-500/15 text-yellow-300 border-yellow-500/40",
  slate:   "bg-slate-500/15 text-slate-300 border-slate-500/40",
};

export const ACTIVITY_META = {
  Call:             { icon: PhoneCall,    tone: "sky" },
  Email:            { icon: Mail,         tone: "blue" },
  "Follow-up":      { icon: CalendarClock, tone: "purple" },
  Chase:            { icon: AlarmClock,   tone: "amber" },
  Quote:            { icon: FileText,     tone: "teal" },
  Payment:          { icon: PoundSterling, tone: "emerald" },
  Delivery:         { icon: Truck,        tone: "orange" },
  "Site visit":     { icon: MapPin,       tone: "rose" },
  Technical:        { icon: Wrench,       tone: "cyan" },
  "Stock check":    { icon: Boxes,        tone: "indigo" },
  Switch:           { icon: Repeat,       tone: "fuchsia" },
  "Cross-sell":     { icon: Shuffle,      tone: "violet" },
  "Account upgrade":{ icon: TrendingUp,   tone: "yellow" },
  Other:            { icon: MessageSquare, tone: "slate" },
  Note:             { icon: StickyNote,   tone: "slate" },
  // legacy (read-only)
  Meeting:          { icon: Video,        tone: "sky" },
  Task:             { icon: ListTodo,     tone: "purple" },
  Deadline:         { icon: AlarmClock,   tone: "rose" },
};

export function metaFor(type) {
  return ACTIVITY_META[type] || ACTIVITY_META.Other;
}

export function toneCls(tone) {
  return TONES[tone] || TONES.slate;
}

export function activityToneCls(type) {
  return toneCls(metaFor(type).tone);
}

// Composer grouping (all creatable types appear exactly once).
export const ACTIVITY_GROUPS = [
  { label: "Talk", types: ["Call", "Email", "Site visit"] },
  { label: "Follow up", types: ["Follow-up", "Chase", "Payment"] },
  { label: "Sell", types: ["Quote", "Cross-sell", "Switch", "Account upgrade"] },
  { label: "Operations", types: ["Delivery", "Stock check", "Technical", "Other"] },
];

// Type-specific fields. kind: chips | text | money | tiers | check. `follow` = suggest booking a follow-up (days).
export const TYPE_FIELDS = {
  Call: { fields: [
    { key: "direction", label: "Direction", kind: "chips", options: ["Outbound", "Inbound"] },
    { key: "outcome", label: "Outcome", kind: "chips", options: ["Connected", "No answer", "Voicemail", "Call back requested", "Wrong number"] },
  ], done: () => true, follow: (d) => (["No answer", "Voicemail", "Call back requested"].includes(d.outcome) ? 1 : null) },
  Email: { fields: [
    { key: "direction", label: "Direction", kind: "chips", options: ["Sent", "Received"] },
    { key: "outcome", label: "Status", kind: "chips", options: ["Awaiting reply", "Replied", "No reply needed"] },
  ], done: () => true, follow: (d) => (d.outcome === "Awaiting reply" ? 2 : null) },
  "Site visit": { fields: [
    { key: "purpose", label: "Purpose", kind: "chips", options: ["Survey", "Measure-up", "Handover", "Problem"] },
    { key: "location", label: "Location", kind: "text", prefill: "address" },
  ] },
  "Follow-up": { fields: [
    { key: "reason", label: "About", kind: "chips", options: ["Quote", "Payment", "Delivery", "Account", "General"] },
    { key: "method", label: "How", kind: "chips", options: ["Call", "Email", "WhatsApp"] },
  ], due: true },
  Chase: { fields: [
    { key: "reason", label: "Chasing", kind: "chips", options: ["Quote decision", "Payment", "Documents", "Supplier"] },
  ], due: true },
  Payment: { fields: [
    { key: "amount", label: "Amount", kind: "money" },
    { key: "method", label: "Method", kind: "chips", options: ["Card", "BACS", "Credit account", "Finance"] },
    { key: "state", label: "Status", kind: "chips", options: ["Promised", "Received", "Overdue"] },
  ], done: (d) => d.state === "Received", due: (d) => d.state !== "Received" },
  Quote: { fields: [
    { key: "quote_ref", label: "Quote ref", kind: "text", prefill: "quoteRef" },
    { key: "stage", label: "Stage", kind: "chips", options: ["Requested", "Sent", "Revised", "Accepted", "Lost"] },
  ], done: (d) => ["Sent", "Accepted", "Lost"].includes(d.stage), follow: (d) => (d.stage === "Sent" ? 2 : null) },
  "Cross-sell": { fields: [
    { key: "products", label: "Products offered", kind: "text" },
    { key: "result", label: "Result", kind: "chips", options: ["Interested", "Added to quote", "Ordered", "Not now"] },
  ], done: () => true, follow: (d) => (d.result === "Interested" ? 3 : null) },
  Switch: { fields: [
    { key: "from", label: "Current supplier", kind: "text" },
    { key: "products", label: "Products", kind: "text" },
    { key: "result", label: "Result", kind: "chips", options: ["Interested", "Trial order", "Switched", "Not now"] },
  ], done: (d) => ["Switched", "Not now"].includes(d.result), follow: (d) => (["Interested", "Trial order"].includes(d.result) ? 7 : null) },
  "Account upgrade": { fields: [
    { key: "to_tier", label: "Upgrade to", kind: "chips", options: ["SILVER", "GOLD", "PLATINUM", "BLACK"] },
    { key: "confirmed", label: "Upgrade confirmed with customer (marks this done)", kind: "check" },
  ], done: (d) => !!d.confirmed, due: (d) => !d.confirmed },
  Delivery: { fields: [
    { key: "order", label: "Order #", kind: "text" },
    { key: "topic", label: "Topic", kind: "chips", options: ["Booking", "ETA update", "Problem / damage", "Missed delivery", "Collection"] },
  ], done: (d) => ["Booking", "ETA update", "Collection"].includes(d.topic) },
  "Stock check": { fields: [
    { key: "product", label: "Product / SKU", kind: "text" },
    { key: "result", label: "Result", kind: "chips", options: ["In stock", "Low stock", "Out of stock", "Lead time"] },
  ], done: () => true },
  Technical: { fields: [
    { key: "product", label: "Product / SKU", kind: "text" },
    { key: "topic", label: "Topic", kind: "chips", options: ["Spec / U-value", "Compatibility", "Install advice", "Warranty"] },
  ], done: () => true },
  Other: { fields: [] },
};

export const autoTitle = (type, d = {}) =>
  [type, ...(TYPE_FIELDS[type]?.fields || []).filter((f) => f.kind !== "check" && d[f.key]).map((f) => (f.kind === "money" ? `£${d[f.key]}` : d[f.key]))]
    .join(" · ").slice(0, 200);
